import React, { useState, useEffect } from 'react';
import { Container, Image, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { PasswordRequirementsBox, buildPasswordSchema, checkPasswordRequirements } from './Utils';
import RestAuthManager from '.';

const API = RestAuthManager.API;

/**
 * Set-a-password screen reached from an email link:
 *   /reset-password?token=<raw>            — self-service "forgot password" reset
 *   /reset-password?token=<raw>&invite=1   — admin-sent invite for a brand-new user
 *
 * Unauthenticated (same HTTP path as /users/login). Validates the token on mount,
 * then lets the user choose a password under the shared password policy.
 */
const ResetPassword = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get('token') || '';
  const isInvite = params.get('invite') === '1';

  // 'checking' | 'valid' | 'invalid'
  const [tokenState, setTokenState] = useState('checking');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [schema, setSchema] = useState(null);
  const [passwordValid, setPasswordValid] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setSchema(buildPasswordSchema());
  }, []);

  useEffect(() => {
    if (schema) {
      setPasswordValid(checkPasswordRequirements(schema, password).valid);
    }
  }, [password, schema]);

  useEffect(() => {
    let cancelled = false;
    const checkToken = async () => {
      if (!token) {
        setTokenState('invalid');
        return;
      }
      try {
        const response = await API.get('api', `/users/reset-token/${encodeURIComponent(token)}`);
        if (cancelled) return;
        setTokenState(response && response.valid === true ? 'valid' : 'invalid');
      } catch (err) {
        console.log('reset-token check failed', err);
        if (cancelled) return;
        setTokenState('invalid');
      }
    };
    checkToken();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await API.post('api', '/users/reset-password', { token, newPassword: password });
      setDone(true);
    } catch (err) {
      // ApiError carries the server's structured envelope on `.body`
      // (400 -> {"error":"Invalid or expired token"}).
      setError(err?.body?.error || err?.message || 'Failed to set password.');
    } finally {
      setSubmitting(false);
    }
  };

  const heading = isInvite ? 'Set your password' : 'Reset your password';

  return (
    <div className='sign-in mh-100 mw-100' data-testid='reset-password-page'>
      <Container>
        <Row className='vh-100'>
          <Col>
            <Image className='login-logo' src='/c2_platform_white.png' />
          </Col>
          <Col className='auth-container my-auto bg-light rounded p-4'>
            <div>
              {tokenState === 'checking' && (
                <div className='text-center p-4'>
                  <Spinner animation='border' variant='primary' />
                </div>
              )}

              {tokenState === 'invalid' && (
                <div data-testid='reset-password-invalid'>
                  <Form.Label className='signin-title'>{heading}</Form.Label>
                  <Alert variant='danger'>
                    This link is invalid or has expired.
                  </Alert>
                  <Link to='/'>Back to sign in</Link>
                </div>
              )}

              {tokenState === 'valid' && done && (
                <div data-testid='reset-password-done'>
                  <Form.Label className='signin-title'>{heading}</Form.Label>
                  <Alert variant='success'>
                    Your password has been set. You can now sign in with it.
                  </Alert>
                  <Link to='/' data-testid='reset-password-signin-link'>Sign in</Link>
                </div>
              )}

              {tokenState === 'valid' && !done && (
                <Form onSubmit={handleSubmit}>
                  <Form.Group>
                    <Form.Label className='signin-title'>{heading}</Form.Label>
                  </Form.Group>
                  {error && <Alert variant='danger'>{error}</Alert>}
                  <Form.Group className='mb-3'>
                    <Form.Label>New password</Form.Label>
                    <Form.Control
                      type='password'
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid='reset-password-new'
                    />
                    {password && (
                      <PasswordRequirementsBox
                        passwordInput={password}
                        confirmInput={confirmPassword}
                      />
                    )}
                  </Form.Group>
                  <Form.Group className='mb-3'>
                    <Form.Label>Confirm password</Form.Label>
                    <Form.Control
                      type='password'
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      data-testid='reset-password-confirm'
                    />
                  </Form.Group>
                  <Button
                    variant='primary'
                    type='submit'
                    disabled={submitting || !passwordValid || password !== confirmPassword}
                    className='w-100'
                    data-testid='reset-password-submit'
                  >
                    {submitting ? 'Saving...' : 'Set Password'}
                  </Button>
                </Form>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ResetPassword;
